import { useEffect, useRef, useState } from 'react';

interface BackgroundMusicProps {
  musicUrl: string | null;
  locationName?: string;
  volume?: number; // 0.0 to 1.0
  autoPlay?: boolean;
}

export default function BackgroundMusic({
  musicUrl,
  locationName,
  volume = 0.015,
  autoPlay = true,
}: BackgroundMusicProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Auto-play when music URL changes
  useEffect(() => {
    if (!audioRef.current || !musicUrl) return;

    const audio = audioRef.current;

    // Reset and load new music
    audio.pause();
    audio.currentTime = 0;
    audio.load();

    if (autoPlay) {
      // Browsers require user interaction before autoplay
      // Try to play, handle if blocked
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.log('Autoplay prevented:', error);
            setIsPlaying(false);
          });
      }
    }
  }, [musicUrl, autoPlay]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  if (!musicUrl) {
    return null; // No music for this location
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      background: 'rgba(15, 12, 41, 0.9)',
      backdropFilter: 'blur(10px)',
      borderRadius: '50px',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    }}>
      <audio
        ref={audioRef}
        src={musicUrl}
        loop
        onEnded={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Music Note Icon */}
      <div style={{
        fontSize: '1.25rem',
        animation: isPlaying ? 'pulse 2s infinite' : 'none',
      }}>
        🎵
      </div>

      {/* Location Name */}
      {locationName && (
        <div style={{
          fontSize: '0.85rem',
          color: '#cbd5e0',
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {locationName}
        </div>
      )}

      {/* Play/Pause Button */}
      <button
        onClick={togglePlay}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(99, 102, 241, 0.2)',
          color: '#cbd5e0',
          fontSize: '0.85rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
        }}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>

      {/* Mute Button */}
      <button
        onClick={toggleMute}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(99, 102, 241, 0.2)',
          color: '#cbd5e0',
          fontSize: '0.85rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
        }}
      >
        {isMuted ? '🔇' : '🔊'}
      </button>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
